import { useState, useCallback, useMemo } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { useAttendanceReports, exportUtils } from '@/hooks/useAttendanceReports';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calendar,
  Users,
  Loader2,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  FileSpreadsheet,
  TrendingUp,
  TrendingDown,
  Gauge,
  Flame,
  Battery,
  CalendarClock,
  UserX,
  AlertTriangle,
  BarChart3,
  PieChart,
  Activity,
  Zap,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart as RechartsBar,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { motion } from 'framer-motion';

// Metric Card - large hero-style metrics
const MetricCard = ({ icon: Icon, label, value, subtitle, status, statusText, color = "slate" }) => {
  const colorConfig = {
    amber: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      icon: "bg-amber-100 text-amber-600",
      value: "text-amber-700",
      status: status === 'warning' ? "text-amber-600" : "text-emerald-600"
    },
    rose: {
      bg: "bg-rose-50",
      border: "border-rose-200",
      icon: "bg-rose-100 text-rose-600",
      value: "text-rose-700",
      status: status === 'warning' ? "text-rose-600" : "text-emerald-600"
    },
    indigo: {
      bg: "bg-indigo-50",
      border: "border-indigo-200",
      icon: "bg-indigo-100 text-indigo-600",
      value: "text-indigo-700",
      status: status === 'warning' ? "text-amber-600" : "text-emerald-600"
    },
    slate: {
      bg: "bg-slate-50",
      border: "border-slate-200",
      icon: "bg-slate-100 text-slate-600",
      value: "text-slate-700",
      status: "text-slate-500"
    }
  };

  const cfg = colorConfig[color];

  return (
    <div className={`relative rounded-2xl ${cfg.bg} border ${cfg.border} p-5 transition-all duration-300 hover:shadow-lg`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2.5 rounded-xl ${cfg.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
        {status && (
          <span className={`text-xs font-medium ${cfg.status}`}>
            {status === 'warning' ? '⚠' : '✓'} {statusText}
          </span>
        )}
      </div>
      <p className="text-sm font-medium text-slate-500 mb-1">{label}</p>
      <p className={`text-3xl font-bold ${cfg.value} tracking-tight`}>{value}</p>
      {subtitle && (
        <p className="text-xs text-slate-400 mt-2">{subtitle}</p>
      )}
    </div>
  );
};

// Efficiency Stat - compact inline stat
const EfficiencyStat = ({ icon: Icon, label, value, subtitle, color = "slate" }) => {
  const colorConfig = {
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700"
  };

  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border ${colorConfig[color]} transition-all duration-300 hover:shadow-md`}>
      <div className="p-2.5 rounded-lg bg-white/80 shadow-sm">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <p className="text-xs font-medium opacity-70">{label}</p>
        <p className="text-xl font-bold">{value}</p>
        {subtitle && <p className="text-xs opacity-60 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
};

// Alert Row - for upcoming absences, forfeiture risk, etc.
const AlertRow = ({ icon: Icon, title, subtitle, badge, badgeVariant = "outline", accentColor = "slate" }) => {
  const accentColors = {
    slate: "bg-slate-400",
    amber: "bg-amber-500",
    rose: "bg-rose-500",
    emerald: "bg-emerald-500",
    indigo: "bg-indigo-500"
  };

  const badgeColors = {
    warning: "bg-amber-100 text-amber-700 border-amber-200",
    danger: "bg-rose-100 text-rose-700 border-rose-200",
    info: "bg-indigo-100 text-indigo-700 border-indigo-200",
    success: "bg-emerald-100 text-emerald-700 border-emerald-200",
    outline: "bg-white text-slate-600 border-slate-200"
  };

  return (
    <div className="group relative bg-white rounded-xl border border-slate-200/80 p-4 pl-5 transition-all duration-300 hover:shadow-md hover:border-slate-300">
      <div className={`absolute left-0 top-3 bottom-3 w-1 ${accentColors[accentColor]} rounded-full`} />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-slate-50">
            <Icon className="h-4 w-4 text-slate-500" />
          </div>
          <div>
            <p className="font-medium text-slate-800 text-[15px]">{title}</p>
            <p className="text-sm text-slate-500">{subtitle}</p>
          </div>
        </div>
        {badge && (
          <span className={`text-xs font-medium px-3 py-1.5 rounded-full border ${badgeColors[badgeVariant]}`}>
            {badge}
          </span>
        )}
      </div>
    </div>
  );
};

// Section Header
const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="p-2 rounded-lg bg-slate-100">
      <Icon className="h-4 w-4 text-slate-600" />
    </div>
    <div>
      <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
      {description && <p className="text-sm text-slate-500">{description}</p>}
    </div>
  </div>
);

// Day of Week Bar
const DayBar = ({ day, count, maxCount, isHighlight }) => {
  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className={`font-medium ${isHighlight ? 'text-amber-600' : 'text-slate-600'}`}>
          {day}
        </span>
        <span className="text-slate-400 tabular-nums">{count} days</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isHighlight ? 'bg-amber-500' : 'bg-slate-400'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Custom Chart Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-lg p-3">
      <p className="text-sm font-semibold text-slate-800 mb-2">{label}</p>
      {payload.map((entry, idx) => (
        <div key={idx} className="flex items-center gap-2 text-sm">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-slate-600">{entry.name}:</span>
          <span className="font-medium text-slate-800">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

export const ReportsPage = () => {
  const { userProfile, company } = useAuth();

  usePageTitle('Attendance Reports');

  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());

  const {
    loading,
    error,
    metrics,
    overview,
    approvalEfficiency,
    atRiskEmployees,
    monthlyTrends,
    byLeaveType,
    forfeitureRisk,
    upcomingAbsences,
    peakAbsenceDays,
    dayOfWeekPattern,
    balances,
    requests,
    years,
    chartColors
  } = useAttendanceReports(company?.id, yearFilter);

  // Memoized chart data
  const statusDistributionData = useMemo(() => {
    if (!overview.totalRequests) return [];
    return [
      { name: 'Approved', value: overview.approvedRequests, color: '#10b981' },
      { name: 'Pending', value: overview.pendingRequests, color: '#f59e0b' },
      { name: 'Rejected', value: overview.rejectedRequests, color: '#64748b' }
    ].filter(item => item.value > 0);
  }, [overview]);

  const balanceChartData = useMemo(() => [{
    name: 'Company Total',
    Entitled: overview.totalEntitled,
    Taken: overview.totalTaken,
    Remaining: overview.totalRemaining
  }], [overview]);

  // Chart colors - refined palette
  const refinedChartColors = ['#475569', '#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0'];

  // Export handlers
  const handleExportCSV = useCallback(async () => {
    try {
      const reportData = {
        metrics, overview, approvalEfficiency, byLeaveType, monthlyTrends,
        forfeitureRisk, upcomingAbsences, atRiskEmployees, peakAbsenceDays,
        dayOfWeekPattern, balances, requests
      };
      await exportUtils.exportCSV(reportData, company?.name, yearFilter);
      toast.success('CSV exported successfully');
    } catch (err) {
      console.error('CSV export error:', err);
      toast.error('Failed to export CSV');
    }
  }, [metrics, overview, approvalEfficiency, byLeaveType, monthlyTrends,
      forfeitureRisk, upcomingAbsences, atRiskEmployees, peakAbsenceDays,
      dayOfWeekPattern, balances, requests, company?.name, yearFilter]);

  const handleExportExcel = useCallback(async () => {
    try {
      toast.info('Generating Excel file...');
      const reportData = {
        metrics, overview, approvalEfficiency, byLeaveType, monthlyTrends,
        forfeitureRisk, upcomingAbsences, atRiskEmployees, peakAbsenceDays,
        dayOfWeekPattern, balances, requests
      };
      await exportUtils.exportExcel(reportData, company?.name, yearFilter);
      toast.success('Excel file exported');
    } catch (err) {
      console.error('Excel export error:', err);
      toast.error('Failed to export Excel');
    }
  }, [metrics, overview, approvalEfficiency, byLeaveType, monthlyTrends,
      forfeitureRisk, upcomingAbsences, atRiskEmployees, peakAbsenceDays,
      dayOfWeekPattern, balances, requests, company?.name, yearFilter]);

  const handleExportPDF = useCallback(async () => {
    try {
      toast.info('Generating PDF...');
      await exportUtils.exportPDF('reports-content-area', yearFilter);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to generate PDF');
    }
  }, [yearFilter]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
            <motion.div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-slate-500 font-medium">Loading reports...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-rose-50 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-rose-500" />
          </div>
          <p className="text-slate-800 font-semibold">Failed to load reports</p>
          <p className="text-slate-500 text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  const maxDayCount = Math.max(...dayOfWeekPattern.map(d => d.count), 1);

  return (
    <div className="space-y-6" id="reports-content-area">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl bg-white border border-slate-200 p-8"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
              Reports & Analytics
            </h1>
            <p className="text-slate-500 mt-1">
              Comprehensive attendance insights for {company?.name || 'your company'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-[120px] h-10 rounded-full border-slate-200">
                <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="h-6 w-px bg-slate-200 mx-1" />

            <Button variant="outline" size="icon" onClick={handleExportCSV} className="h-10 w-10 rounded-full border-slate-200" title="Export CSV">
              <FileText className="h-4 w-4 text-slate-600" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleExportExcel} className="h-10 w-10 rounded-full border-slate-200" title="Export Excel">
              <FileSpreadsheet className="h-4 w-4 text-slate-600" />
            </Button>
            <Button onClick={handleExportPDF} className="h-10 bg-slate-800 hover:bg-slate-700 rounded-full px-5">
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            icon={Gauge}
            label="Absenteeism Rate"
            value={`${metrics.absenteeismRate}%`}
            subtitle="UK avg: 2.6% • Target: <3%"
            status={metrics.absenteeismRate > 3 ? 'warning' : 'ok'}
            statusText={metrics.absenteeismRate > 3 ? 'Above target' : 'Within target'}
            color="amber"
          />
          <MetricCard
            icon={Flame}
            label="Sick Leave Rate"
            value={`${metrics.sickLeaveRate}%`}
            subtitle="Unplanned absences • UK avg: 1.8%"
            status={metrics.sickLeaveRate > 2 ? 'warning' : 'ok'}
            statusText={metrics.sickLeaveRate > 2 ? 'Above average' : 'Normal range'}
            color="rose"
          />
          <MetricCard
            icon={Battery}
            label="Leave Utilization"
            value={`${metrics.utilizationRate}%`}
            subtitle="Of entitled leave taken • Target: 75-90%"
            status={metrics.utilizationRate < 60 || metrics.utilizationRate > 95 ? 'warning' : 'ok'}
            statusText={metrics.utilizationRate < 60 ? 'Low usage' : metrics.utilizationRate > 95 ? 'Very high' : 'Healthy'}
            color="indigo"
          />
        </div>
      </motion.div>

      {/* Approval Efficiency */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        <SectionHeader
          icon={Zap}
          title="Approval Efficiency"
          description="How quickly requests are being processed"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <EfficiencyStat
            icon={Clock}
            label="Avg Approval Time"
            value={`${approvalEfficiency.avgApprovalTime}h`}
            subtitle="Target: <24h"
            color="indigo"
          />
          <EfficiencyStat
            icon={AlertCircle}
            label="Pending Requests"
            value={overview.pendingRequests}
            subtitle="Awaiting approval"
            color="amber"
          />
          <EfficiencyStat
            icon={XCircle}
            label="Overdue (>48h)"
            value={approvalEfficiency.pendingOverdue}
            subtitle="Needs attention"
            color="rose"
          />
        </div>
      </motion.div>

      {/* Two Column Layout: Upcoming Absences & Forfeiture Risk */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Absences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="rounded-2xl bg-white border border-slate-200 p-6"
        >
          <SectionHeader
            icon={CalendarClock}
            title="Upcoming Absences"
            description="Next 30 days • Capacity planning"
          />
          {upcomingAbsences.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-slate-50 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-slate-300" />
              </div>
              <p className="text-slate-500 text-sm">No approved absences in the next 30 days</p>
            </div>
          ) : (
            <div className="space-y-2">
              {upcomingAbsences.slice(0, 5).map((absence, idx) => (
                <AlertRow
                  key={idx}
                  icon={Calendar}
                  title={absence.name}
                  subtitle={`${absence.type} • ${absence.days} day${absence.days > 1 ? 's' : ''}`}
                  badge={new Date(absence.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  badgeVariant="outline"
                  accentColor="indigo"
                />
              ))}
            </div>
          )}
        </motion.div>

        {/* Forfeiture Risk */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="rounded-2xl bg-white border border-slate-200 p-6"
        >
          <SectionHeader
            icon={AlertTriangle}
            title="Forfeiture Risk"
            description="Employees at risk of losing entitlement"
          />
          {forfeitureRisk.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-400" />
              </div>
              <p className="text-slate-500 text-sm">No employees at risk of forfeiting leave</p>
            </div>
          ) : (
            <div className="space-y-2">
              {forfeitureRisk.slice(0, 5).map((emp, idx) => (
                <AlertRow
                  key={idx}
                  icon={UserX}
                  title={emp.name}
                  subtitle={`${emp.daysAtRisk} days unused • ${emp.daysUntilYearEnd} days until year end`}
                  badge="Action Needed"
                  badgeVariant="danger"
                  accentColor="rose"
                />
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* At-Risk Employees: Burnout & Low Balance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Burnout Risk */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="rounded-2xl bg-white border border-slate-200 p-6"
        >
          <SectionHeader
            icon={Flame}
            title="Burnout Risk"
            description="Low leave usage (<25%)"
          />
          {atRiskEmployees.burnoutRisk.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-400" />
              </div>
              <p className="text-slate-500 text-sm">All employees taking adequate leave</p>
            </div>
          ) : (
            <div className="space-y-2">
              {atRiskEmployees.burnoutRisk.slice(0, 5).map((emp, idx) => (
                <AlertRow
                  key={idx}
                  icon={Users}
                  title={emp.name}
                  subtitle={`${emp.remaining} days unused • ${emp.usageRate}% used`}
                  badge="Monitor"
                  badgeVariant="warning"
                  accentColor="amber"
                />
              ))}
            </div>
          )}
        </motion.div>

        {/* Low Balance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="rounded-2xl bg-white border border-slate-200 p-6"
        >
          <SectionHeader
            icon={Battery}
            title="Low Balance Alert"
            description="<5 days remaining (>80% used)"
          />
          {atRiskEmployees.lowBalance.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-400" />
              </div>
              <p className="text-slate-500 text-sm">No employees with critically low balance</p>
            </div>
          ) : (
            <div className="space-y-2">
              {atRiskEmployees.lowBalance.slice(0, 5).map((emp, idx) => (
                <AlertRow
                  key={idx}
                  icon={Users}
                  title={emp.name}
                  subtitle={`${emp.remaining} days left • ${emp.usageRate}% used`}
                  badge="Low Balance"
                  badgeVariant="info"
                  accentColor="indigo"
                />
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Peak Absence Days */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.35 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        <SectionHeader
          icon={Users}
          title="Peak Absence Days"
          description="Days with most employees absent (coverage risk)"
        />
        {peakAbsenceDays.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-slate-50 flex items-center justify-center">
              <Calendar className="h-6 w-6 text-slate-300" />
            </div>
            <p className="text-slate-500 text-sm">No significant absence peaks identified</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {peakAbsenceDays.slice(0, 8).map((peak, idx) => (
              <AlertRow
                key={idx}
                icon={Calendar}
                title={new Date(peak.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                subtitle={`${peak.count} employee${peak.count > 1 ? 's' : ''} absent`}
                badge={peak.count >= 5 ? 'High Risk' : peak.count >= 3 ? 'Medium' : 'Low'}
                badgeVariant={peak.count >= 5 ? 'danger' : peak.count >= 3 ? 'warning' : 'outline'}
                accentColor={peak.count >= 5 ? 'rose' : peak.count >= 3 ? 'amber' : 'slate'}
              />
            ))}
          </div>
        )}
      </motion.div>

      {/* Sick Leave Pattern */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        <SectionHeader
          icon={Activity}
          title="Sick Leave Pattern"
          description="Day of week distribution • Identifies potential patterns"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            {dayOfWeekPattern.map((day, idx) => (
              <DayBar
                key={idx}
                day={day.day}
                count={day.count}
                maxCount={maxDayCount}
                isHighlight={day.day === 'Mon' || day.day === 'Fri'}
              />
            ))}
          </div>
          <div className="flex flex-col justify-center">
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Pattern Insight</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Elevated Monday/Friday sick leave may indicate wellbeing issues or work patterns requiring attention.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leave Type Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="rounded-2xl bg-white border border-slate-200 p-6"
        >
          <SectionHeader
            icon={PieChart}
            title="Leave Type Distribution"
            description="Breakdown by type"
          />
          {byLeaveType.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <RechartsPie>
                <Pie
                  data={byLeaveType}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={2}
                  label={({ type, count }) => `${type}: ${count}`}
                  labelLine={false}
                >
                  {byLeaveType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={refinedChartColors[index % refinedChartColors.length]} />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomTooltip />} />
              </RechartsPie>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-slate-400">
              No data available
            </div>
          )}
        </motion.div>

        {/* Status Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="rounded-2xl bg-white border border-slate-200 p-6"
        >
          <SectionHeader
            icon={Target}
            title="Request Status"
            description="Current status breakdown"
          />
          {statusDistributionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <RechartsPie>
                <Pie
                  data={statusDistributionData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={2}
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}
                >
                  {statusDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomTooltip />} />
              </RechartsPie>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-slate-400">
              No data available
            </div>
          )}
        </motion.div>
      </div>

      {/* Monthly Trends */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.55 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        <SectionHeader
          icon={TrendingUp}
          title="Monthly Trends"
          description="Leave request patterns throughout the year"
        />
        {monthlyTrends.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="monthName" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={{ stroke: '#e2e8f0' }} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="requests" stroke="#475569" strokeWidth={2} dot={{ fill: '#475569', r: 4 }} name="Total" />
              <Line type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} name="Approved" />
              <Line type="monotone" dataKey="rejected" stroke="#94a3b8" strokeWidth={2} dot={{ fill: '#94a3b8', r: 4 }} name="Rejected" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[320px] text-slate-400">
            No data available
          </div>
        )}
      </motion.div>

      {/* Leave Type Status Breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        <SectionHeader
          icon={BarChart3}
          title="Leave Type Breakdown"
          description="Status distribution for each leave type"
        />
        {byLeaveType.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <RechartsBar data={byLeaveType} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="type" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} angle={-30} textAnchor="end" height={80} />
              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={{ stroke: '#e2e8f0' }} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="approved" fill="#10b981" name="Approved" radius={[4, 4, 0, 0]} />
              <Bar dataKey="pending" fill="#f59e0b" name="Pending" radius={[4, 4, 0, 0]} />
              <Bar dataKey="rejected" fill="#94a3b8" name="Rejected" radius={[4, 4, 0, 0]} />
            </RechartsBar>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[320px] text-slate-400">
            No data available
          </div>
        )}
      </motion.div>

      {/* Leave Balance Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.65 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        <SectionHeader
          icon={Battery}
          title="Leave Balance Overview"
          description="Company-wide entitlement vs usage"
        />

        {/* Balance Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200">
            <p className="text-sm font-medium text-indigo-600">Total Entitled</p>
            <p className="text-3xl font-bold text-indigo-800 mt-1">{overview.totalEntitled}</p>
            <p className="text-xs text-indigo-500 mt-1">Days allocated</p>
          </div>
          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
            <p className="text-sm font-medium text-emerald-600">Total Taken</p>
            <p className="text-3xl font-bold text-emerald-800 mt-1">{overview.totalTaken}</p>
            <p className="text-xs text-emerald-500 mt-1">Days used</p>
          </div>
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-sm font-medium text-amber-600">Total Remaining</p>
            <p className="text-3xl font-bold text-amber-800 mt-1">{overview.totalRemaining}</p>
            <p className="text-xs text-amber-500 mt-1">Days available</p>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={200}>
          <RechartsBar data={balanceChartData} layout="vertical" barSize={40}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
            <XAxis type="number" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={{ stroke: '#e2e8f0' }} />
            <YAxis type="category" dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={{ stroke: '#e2e8f0' }} width={100} />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="Entitled" fill="#6366f1" name="Entitled" radius={[0, 4, 4, 0]} />
            <Bar dataKey="Taken" fill="#10b981" name="Taken" radius={[0, 4, 4, 0]} />
            <Bar dataKey="Remaining" fill="#f59e0b" name="Remaining" radius={[0, 4, 4, 0]} />
          </RechartsBar>
        </ResponsiveContainer>
      </motion.div>
    </div>
  );
};
